// src/components/Admin/ProfileSettings.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Tabs,
  Tab,
  styled,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Security,
  Settings,
  Notifications,
  Save,
  Edit,
  Visibility,
  VisibilityOff,
  Login,
  Assignment,
  Description,
  PhotoCamera,
  Delete,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import api from '../../services/api';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
};

const GlassCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0, 0, 0, 0.25)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)',
  backgroundImage: 'none',
}));

function ProfileSettings() {
  const { user: authUser, refreshUser, updateUser } = useAuth();
  const { setMode: setSharedThemeMode } = useThemeMode();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userData, setUserData] = useState(authUser);

  const [profileData, setProfileData] = useState({
    firstName: authUser?.firstName || '',
    lastName: authUser?.lastName || '',
    email: authUser?.email || '',
    phone: authUser?.phoneNumber || '',
    facility: authUser?.facilityName || '',
    facilityType: authUser?.facilityType || '',
  });

  // Fetch fresh user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get('/auth/me');
        if (response.data.success && response.data.user) {
          setUserData(response.data.user);
          setProfileData({
            firstName: response.data.user.firstName || '',
            lastName: response.data.user.lastName || '',
            email: response.data.user.email || '',
            phone: response.data.user.phoneNumber || '',
            facility: response.data.user.facility?.facilityName || '',
            facilityType: response.data.user.facilityType || '',
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };
    fetchUserData();
  }, []);

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [preferences, setPreferences] = useState({
    textSize: 'medium',
    emailNotifications: true,
    inAppNotifications: true,
    notificationPreference: 'both', // 'email', 'inApp', 'both'
    darkMode: true,
  });

  const [activityLog, setActivityLog] = useState({
    loginHistory: [],
    studies: [],
    reports: [],
  });

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSecurityChange = (e) => {
    setSecurityData({
      ...securityData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePreferenceChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      setPreferences({
        ...preferences,
        [name]: checked,
      });
    } else {
      setPreferences({
        ...preferences,
        [name]: value,
      });
    }
  };

  useEffect(() => {
    // Load preferences
    const loadPreferences = async () => {
      try {
        const response = await api.get('/users/preferences');
        if (response.data && response.data.success !== false) {
          const prefs = response.data;
          setPreferences(prev => ({
            ...prev,
            textSize: prefs.textSize || 'medium',
            emailNotifications: prefs.emailNotifications ?? prefs.notifications?.email ?? true,
            inAppNotifications: prefs.inAppNotifications ?? prefs.notifications?.push ?? true,
            notificationPreference: prefs.notificationPreference || 'both',
            darkMode: prefs.darkMode ?? (prefs.theme === 'dark'),
          }));
          // Apply text size immediately
          applyTextSize(prefs.textSize || 'medium');
        }
      } catch (err) {
        // Silently handle preferences loading errors - use defaults
        if (err.response?.status !== 400) {
          console.error('Error loading preferences:', err);
        }
        // Apply default text size
        applyTextSize('medium');
      }
    };
    
    // Load activity log data
    const loadActivityLog = async () => {
      try {
        const response = await api.get('/users/activity');
        if (response.data && response.data.success) {
          setActivityLog({
            loginHistory: response.data.loginHistory || [],
            studies: response.data.studies || [],
            reports: response.data.reports || [],
          });
        }
      } catch (err) {
        console.error('Error loading activity log:', err);
        // Set empty arrays on error
        setActivityLog({
          loginHistory: [],
          studies: [],
          reports: [],
        });
      }
    };
    
    loadPreferences();
    loadActivityLog();
  }, []);

  // Apply text size globally via CSS variable
  const applyTextSize = (size) => {
    const sizeMap = {
      small: '0.875rem',
      medium: '1rem',
      large: '1.125rem',
    };
    const baseSize = sizeMap[size] || sizeMap.medium;
    document.documentElement.style.setProperty('--text-size-base', baseSize);
    document.documentElement.style.setProperty('--text-size-multiplier', size === 'small' ? '0.875' : size === 'large' ? '1.125' : '1');
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phone || undefined, // Send undefined instead of empty string
        institutionRole: profileData.institutionRole || undefined,
      };

      // Remove undefined and empty string values
      const cleanedData = {};
      Object.keys(updateData).forEach(key => {
        if (updateData[key] !== undefined && updateData[key] !== '' && updateData[key] !== null) {
          cleanedData[key] = updateData[key];
        }
      });

      console.log('Sending profile update:', cleanedData);
      const response = await api.put('/auth/profile', cleanedData);
      if (response.data.success) {
        const updatedUser = response.data.user;
        setUserData(updatedUser);
        // Update localStorage and AuthContext
        localStorage.setItem('user', JSON.stringify(updatedUser));
        if (updateUser) {
          updateUser(updatedUser);
        } else if (refreshUser) {
          refreshUser();
        }
        setSuccess(t('profile.profileUpdatedSuccess') || 'Profile updated successfully');
      }
    } catch (err) {
      console.error('Profile update error:', err.response?.data);
      const errorMessage = err.response?.data?.error || 
                          (err.response?.data?.details && Array.isArray(err.response.data.details) 
                            ? err.response.data.details.map(d => d.msg || d).join(', ')
                            : err.response?.data?.details) ||
                          err.response?.data?.message || 
                          t('profile.failedToUpdateProfile');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('profilePicture', file);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/auth/profile/picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        const newProfilePictureUrl = response.data.profilePicture;
        console.log('Profile picture uploaded successfully:', newProfilePictureUrl);
        
        // Refresh user data from API to ensure we get the correct user's data
        try {
          const userResponse = await api.get('/auth/me');
          if (userResponse.data.success && userResponse.data.user) {
            const updatedUser = userResponse.data.user;
            console.log('Refreshed user data:', updatedUser._id, updatedUser.email, updatedUser.profilePicture);
            
            // Verify this is the same user
            if (updatedUser._id === userData?._id || updatedUser.email === userData?.email) {
              // Update state with fresh data from API
              setUserData(updatedUser);
              // Update AuthContext and localStorage
              if (updateUser) {
                updateUser(updatedUser);
              } else if (refreshUser) {
                refreshUser();
              } else {
                localStorage.setItem('user', JSON.stringify(updatedUser));
              }
            } else {
              console.warn('User ID mismatch - not updating profile picture');
              setError('User verification failed. Please refresh the page.');
            }
          } else {
            // Fallback: update state and localStorage with new picture
            const updatedUser = { ...userData, profilePicture: newProfilePictureUrl };
            setUserData(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
          // Fallback: update state and localStorage with new picture
          const updatedUser = { ...userData, profilePicture: newProfilePictureUrl };
          setUserData(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        setSuccess('Profile picture updated successfully!');
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error uploading picture:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Failed to upload picture. Please check that file storage is configured.';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleDeletePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      await api.delete('/auth/profile/picture');
      setUserData(prev => ({ ...prev, profilePicture: null }));
      const updatedUser = { ...userData, profilePicture: null };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setSuccess('Profile picture deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting picture:', err);
      setError('Failed to delete picture');
    }
  };

  const handleChangePassword = async () => {
    // Validate required fields
    if (!securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword) {
      setError(t('profile.security.allFieldsRequired') || 'All fields are required');
      return;
    }

    if (securityData.newPassword !== securityData.confirmPassword) {
      setError(t('profile.security.passwordsDoNotMatch'));
      return;
    }

    // Validate password requirements
    if (securityData.newPassword.length < 6) {
      setError(t('profile.security.passwordMinLength') || 'Password must be at least 6 characters long');
      return;
    }

    const hasLowerCase = /[a-z]/.test(securityData.newPassword);
    const hasUpperCase = /[A-Z]/.test(securityData.newPassword);
    const hasNumber = /\d/.test(securityData.newPassword);

    if (!hasLowerCase || !hasUpperCase || !hasNumber) {
      setError(t('profile.security.passwordRequirements') || 'Password must contain at least one lowercase letter, one uppercase letter, and one number');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/auth/change-password', {
        currentPassword: securityData.currentPassword,
        newPassword: securityData.newPassword,
      });
      setSuccess(t('profile.security.passwordChangedSuccess'));
      setSecurityData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      // Backend returns 'error' and 'details' fields, not 'message'
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.details || 
                          err.response?.data?.message || 
                          t('profile.security.failedToChangePassword');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.patch('/users/preferences', preferences);
      console.log('Preferences save response:', response.data);
      
      // Apply text size immediately after saving
      const savedTextSize = response.data?.textSize || preferences.textSize;
      applyTextSize(savedTextSize);
      
      // Extract theme from response - check multiple possible locations
      const themeFromResponse = response.data?.theme || 
                                response.data?.preferences?.theme || 
                                (response.data?.darkMode ? 'dark' : 'light');
      
      // Use theme from response if available, otherwise use current preference
      const themeValue = themeFromResponse || (preferences.darkMode ? 'dark' : 'light');
      
      // Apply immediately via the shared theme context (persists to localStorage itself)
      setSharedThemeMode(themeValue);

      // Dispatch custom event to notify other open tabs/components about the theme change
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: themeValue } }));

      // Reload preferences to ensure we have the latest from server
      const reloadResponse = await api.get('/users/preferences');
      if (reloadResponse.data && reloadResponse.data.success !== false) {
        const prefs = reloadResponse.data;
        const reloadedTheme = prefs.theme || (prefs.darkMode ? 'dark' : 'light');

        if (reloadedTheme && (reloadedTheme === 'light' || reloadedTheme === 'dark')) {
          setSharedThemeMode(reloadedTheme);
        }

        setPreferences(prev => ({
          ...prev,
          textSize: prefs.textSize || 'medium',
          emailNotifications: prefs.emailNotifications ?? prefs.notifications?.email ?? true,
          inAppNotifications: prefs.inAppNotifications ?? prefs.notifications?.push ?? true,
          notificationPreference: prefs.notificationPreference || 'both',
          darkMode: prefs.darkMode ?? (prefs.theme === 'dark'),
        }));
      }
      
      setSuccess(t('profile.preferences.preferencesSavedSuccess'));
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('profile.preferences.failedToSavePreferences'));
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('profile.personalInformation')}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        {t('profile.updatePersonalDetails')}
      </Typography>

      <Box display="flex" alignItems="center" gap={3} mb={4}>
        <Box sx={{ position: 'relative' }}>
          {userData?.profilePicture ? (
            <Box
              key={`profile-pic-${userData._id || userData.id}-${userData.profilePicture}`}
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundImage: `url(${userData.profilePicture})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                border: '4px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)',
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 4px 20px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  : '0 4px 20px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(0, 0, 0, 0.05)',
              }}
            />
          ) : (
            <Avatar
              key={`avatar-${userData?._id || userData?.id || 'no-user'}`}
              sx={{
                width: 100,
                height: 100,
                background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
                fontSize: '2rem',
                border: '4px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)',
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 4px 20px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  : '0 4px 20px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(0, 0, 0, 0.05)',
              }}
            >
              {userData?.firstName?.charAt(0)}{userData?.lastName?.charAt(0)}
            </Avatar>
          )}
          {uploading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
                zIndex: 1,
              }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {userData?.firstName} {userData?.lastName}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" mb={1}>
            {t('profile.memberSince')} {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : t('profile.na')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              {...getRootProps()}
              sx={{
                display: 'inline-block',
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhotoCamera />}
                sx={{ borderRadius: '8px' }}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : t('profile.changePhoto')}
              </Button>
            </Box>
            {userData?.profilePicture && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete />}
                sx={{ borderRadius: '8px' }}
                color="error"
                onClick={handleDeletePicture}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('profile.firstName')}
            name="firstName"
            value={profileData.firstName}
            onChange={handleProfileChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('profile.lastName')}
            name="lastName"
            value={profileData.lastName}
            onChange={handleProfileChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profile.email')}
            name="email"
            type="email"
            value={profileData.email}
            onChange={handleProfileChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('profile.phone')}
            name="phone"
            value={profileData.phone}
            onChange={(e) => {
              setProfileData({
                ...profileData,
                phone: e.target.value,
              });
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('profile.facility')}
            name="facility"
            value={profileData.facility}
            disabled
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t('profile.facilityType')}</InputLabel>
            <Select
              name="facilityType"
              value={profileData.facilityType}
              onChange={handleProfileChange}
              label={t('profile.facilityType')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="hospital">{t('profile.facilityTypes.hospital')}</MenuItem>
              <MenuItem value="clinic">{t('profile.facilityTypes.clinic')}</MenuItem>
              <MenuItem value="imaging_center">{t('profile.facilityTypes.imagingCenter')}</MenuItem>
              <MenuItem value="private_practice">{t('profile.facilityTypes.privatePractice')}</MenuItem>
              <MenuItem value="other">{t('profile.facilityTypes.other')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box mt={4}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSaveProfile}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
            px: 4,
          }}
        >
          {t('profile.saveChanges')}
        </Button>
      </Box>
    </Box>
  );

  const renderSecurityTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('profile.security.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        {t('profile.security.description')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profile.security.currentPassword')}
            name="currentPassword"
            type={showPassword ? 'text' : 'password'}
            value={securityData.currentPassword}
            onChange={handleSecurityChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profile.security.newPassword')}
            name="newPassword"
            type={showPassword ? 'text' : 'password'}
            value={securityData.newPassword}
            onChange={handleSecurityChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profile.security.confirmPassword')}
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={securityData.confirmPassword}
            onChange={handleSecurityChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Grid>
      </Grid>

      <Box mt={4}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleChangePassword}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
            px: 4,
          }}
        >
          {t('common.save')}
        </Button>
      </Box>
    </Box>
  );

  const renderPreferencesTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('profile.preferences.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        {t('profile.preferences.customizeExperience')}
      </Typography>

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary" mb={2}>
          {t('profile.preferences.textSize')}
        </Typography>
        <Box sx={{ px: 2, mb: 4 }}>
          <Slider
            value={preferences.textSize === 'small' ? 0 : preferences.textSize === 'medium' ? 1 : 2}
            onChange={(e, value) => {
              const sizes = ['small', 'medium', 'large'];
              const newSize = sizes[value];
              setPreferences({
                ...preferences,
                textSize: newSize,
              });
              // Apply text size immediately when slider changes
              applyTextSize(newSize);
            }}
            marks={[
              { value: 0, label: t('profile.preferences.small') },
              { value: 1, label: t('profile.preferences.medium') },
              { value: 2, label: t('profile.preferences.large') },
            ]}
            min={0}
            max={2}
            step={1}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary" mb={2}>
          {t('profile.preferences.notifications')}
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            name="notificationPreference"
            value={preferences.notificationPreference}
            onChange={handlePreferenceChange}
          >
            <FormControlLabel
              value="email"
              control={<Radio />}
              label={t('profile.preferences.emailOnly')}
            />
            <Typography variant="caption" color="textSecondary" display="block" ml={4} mb={1}>
              {t('profile.preferences.emailDescription')}
            </Typography>
            <FormControlLabel
              value="inApp"
              control={<Radio />}
              label={t('profile.preferences.inAppOnly')}
            />
            <Typography variant="caption" color="textSecondary" display="block" ml={4} mb={1}>
              {t('profile.preferences.inAppDescription')}
            </Typography>
            <FormControlLabel
              value="both"
              control={<Radio />}
              label={t('profile.preferences.both')}
            />
            <Typography variant="caption" color="textSecondary" display="block" ml={4} mb={2}>
              {t('profile.preferences.bothDescription')}
            </Typography>
          </RadioGroup>
        </FormControl>
      </Box>

      <Box mt={4}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSavePreferences}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
            px: 4,
          }}
        >
          {t('common.save')} {t('profile.preferences.title')}
        </Button>
      </Box>
    </Box>
  );

  const renderActivityTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('profile.activity.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        {t('profile.activity.description')}
      </Typography>

      {/* Login History */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Login color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('profile.activity.loginHistory')}
          </Typography>
        </Box>
        <TableContainer component={Card} sx={{ borderRadius: '12px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('profile.activity.dateTime')}</TableCell>
                <TableCell>{t('profile.activity.ipAddress')}</TableCell>
                <TableCell>{t('profile.activity.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLog.loginHistory.length > 0 ? (
                activityLog.loginHistory.slice(0, 10).map((login, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {login.date ? new Date(login.date).toLocaleString() : t('profile.na')}
                    </TableCell>
                    <TableCell>{login.ipAddress || t('profile.na')}</TableCell>
                    <TableCell>
                      <Chip label={login.status || t('profile.activity.success')} color="success" size="small" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('profile.activity.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Studies Created or Modified */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Assignment color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('profile.activity.studiesCreated')}
          </Typography>
        </Box>
        <TableContainer component={Card} sx={{ borderRadius: '12px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('profile.activity.studyId')}</TableCell>
                <TableCell>{t('profile.activity.patientName')}</TableCell>
                <TableCell>{t('profile.activity.date')}</TableCell>
                <TableCell>{t('profile.activity.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLog.studies.length > 0 ? (
                activityLog.studies.slice(0, 10).map((study, index) => (
                  <TableRow key={index}>
                    <TableCell>{study.studyId || t('profile.na')}</TableCell>
                    <TableCell>{study.patientName || t('profile.na')}</TableCell>
                    <TableCell>
                      {study.date ? new Date(study.date).toLocaleDateString() : t('profile.na')}
                    </TableCell>
                    <TableCell>
                      <Chip label={study.action || t('profile.activity.modified')} color="info" size="small" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('profile.activity.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Reports Submitted */}
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Description color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('profile.activity.reportsSubmitted')}
          </Typography>
        </Box>
        <TableContainer component={Card} sx={{ borderRadius: '12px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('profile.activity.reportId')}</TableCell>
                <TableCell>{t('profile.activity.studyId')}</TableCell>
                <TableCell>{t('profile.activity.date')}</TableCell>
                <TableCell>{t('profile.activity.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLog.reports.length > 0 ? (
                activityLog.reports.slice(0, 10).map((report, index) => (
                  <TableRow key={index}>
                    <TableCell>{report.reportId || t('profile.na')}</TableCell>
                    <TableCell>{report.studyId || t('profile.na')}</TableCell>
                    <TableCell>
                      {report.date ? new Date(report.date).toLocaleDateString() : t('profile.na')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status || t('profile.activity.submitted')}
                        color={report.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('profile.activity.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
          {t('profile.title')}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem' }}>
          {t('profile.manageAccount')}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <GlassCard>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab icon={<Person />} label={t('profile.title')} iconPosition="start" />
            <Tab icon={<Security />} label={t('profile.security.title')} iconPosition="start" />
            <Tab icon={<Settings />} label={t('profile.preferences.title')} iconPosition="start" />
            <Tab icon={<Notifications />} label={t('profile.activity.title')} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          {tabValue === 0 && renderProfileTab()}
          {tabValue === 1 && renderSecurityTab()}
          {tabValue === 2 && renderPreferencesTab()}
          {tabValue === 3 && renderActivityTab()}
        </CardContent>
      </GlassCard>
    </Box>
  );
}

export default ProfileSettings;